'use client';

// Soft-tinted circular platform buttons with a label below, rendered
// inside `<ShareModal>`. Internal — not exported from the lib's public
// surface. Layout per Figma: 44 px circle (16 px icon) + 10 px label,
// stacked vertically, 56 px column width, gap-4 between columns.
import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { Icon } from '@/components/ui/icon';
import { LinkForwardIconSolid } from '@/components/ui/icon/glyphs';
import { cn } from '@/lib/ui/utils';
import { PLATFORM_REGISTRY, type PlatformChannel } from './share-platforms';
const NATIVE_TINT_CLASS = 'bg-background-brand text-foreground-brand group-hover:bg-background-brand/80' as const;
export interface SharePlatformRowProps {
  /** Channels to render in the row, in order. */
  channels: readonly PlatformChannel[];
  /** Whether to render the native-share (OS sheet) button after the channels. */
  showNative: boolean;
  /** Channel-button click handler. */
  onChannel: (channel: PlatformChannel) => void;
  /** Native-share click handler. */
  onNative: () => void;
}
interface PlatformButtonProps {
  /** Long aria-label (translated, e.g. "Share on Telegram"). */
  ariaLabel: string;
  /** Short brand name rendered visibly under the circle. */
  name: string;
  tintClass: string;
  icon: typeof LinkForwardIconSolid;
  onClick: () => void;
}
function PlatformButton(props) {
  const $ = _c(14);
  const t0 = props.ariaLabel;
  const t1 = props.onClick;
  let t2;
  if ($[0] !== props.tintClass) {
    t2 = cn("flex size-11 items-center justify-center rounded-full transition-colors", props.tintClass);
    $[0] = props.tintClass;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== props.icon) {
    t3 = <Icon icon={props.icon} className="size-4" aria-hidden="true" />;
    $[2] = props.icon;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = <span className={t2}>{t3}</span>;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== props.name) {
    t5 = <span className="block w-full truncate text-center text-micro text-foreground-tertiary">{props.name}</span>;
    $[7] = props.name;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== props.ariaLabel || $[10] !== props.onClick || $[11] !== t4 || $[12] !== t5) {
    t6 = <button type="button" aria-label={t0} onClick={t1} className="group flex w-14 cursor-pointer flex-col items-center gap-1 rounded-overlay-item outline-none focus-visible:ring-[3px] focus-visible:ring-border-primary/30">{t4}{t5}</button>;
    $[9] = props.ariaLabel;
    $[10] = props.onClick;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
}
export function SharePlatformRow(props) {
  const $ = _c(9);
  const t = useTranslations("share.modal");
  let t0;
  if ($[0] !== props || $[1] !== t) {
    t0 = props.channels.map(c => {
      const descriptor = PLATFORM_REGISTRY[c];
      return <PlatformButton key={c} ariaLabel={t(c)} name={descriptor.name} tintClass={descriptor.tintClass} icon={descriptor.icon} onClick={() => {
        props.onChannel(c);
      }} />;
    });
    $[0] = props;
    $[1] = t;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== props || $[4] !== t) {
    t1 = props.showNative && <PlatformButton ariaLabel={t("native")} name={t("nativeShort")} tintClass={NATIVE_TINT_CLASS} icon={LinkForwardIconSolid} onClick={() => {
      props.onNative();
    }} />;
    $[3] = props;
    $[4] = t;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== t0 || $[7] !== t1) {
    t2 = <div className="flex flex-wrap items-start justify-between gap-2">{t0}{t1}</div>;
    $[6] = t0;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
}